import React from 'react';
import * as mobx from 'mobx';
import { FormattedMessage, IntlShape } from 'react-intl';
import * as IoC from 'typescript-ioc';
import { IErrorParsing } from '../../../infrastructure/error-parsing';
import { alertServerError, showNotification } from '../../global/modal/alerts-host/alert-templates';
import { IAlertsService } from '../../../infrastructure/alerts';
import { IRolesDAO, Permission, RolesAction } from '../../../api/roles.dao';
import { Space } from '../../../constants/html-character-entities';
import { RoleFormDataVM } from './role-form-data.viewmodel';
import TrackingServiceProvider from '../../../tracking-service/tracking-service-provider';
import { TRACKING_EVENTS } from '../../../tracking-service/tracking-values';

class RoleFormVM {

    @mobx.observable
    public isSaving: boolean;

    @mobx.observable
    public isLoading: boolean;

    @mobx.observable
    public roleFormDataVM: RoleFormDataVM;

    @mobx.observable
    public linkName: string;

    public permissions: Permission[];

    constructor(
        private fetchRolesList?: () => void,
        private closeEditRoleForm?: (linkName: string) => void,
        @IoC.Inject private readonly rolesDAO: IRolesDAO = IoC.Container.get(IRolesDAO),
        @IoC.Inject private readonly errorParsingService: IErrorParsing = IoC.Container.get(IErrorParsing),
        @IoC.Inject private readonly alertsService: IAlertsService = IoC.Container.get(IAlertsService),
        @IoC.Inject private readonly trackingService: TrackingServiceProvider = IoC.Container.get(TrackingServiceProvider),
    ) {
        this.roleFormDataVM = new RoleFormDataVM();
        this.isLoading = true;
    }

    public getErrorMessage(intl: IntlShape): string | null {
        const {showValidationErrors, getNameError, getDescriptionError, serverErrorMessage} = this.roleFormDataVM;
        if (showValidationErrors()) {
            if (getNameError(intl)) {
                return getNameError(intl);
            } else if (getDescriptionError(intl)) {
                return getDescriptionError(intl);
            }
        }
        return serverErrorMessage;
    }

    public setRole = async (linkName?: string) => {
        this.roleFormDataVM = new RoleFormDataVM();
        if (linkName) {
            const roleDetails = await this.rolesDAO.getPermissionsByRoleId({linkName: linkName});
            mobx.runInAction(() => {
                this.linkName = linkName;
                this.permissions = roleDetails.permissions;
            });
            this.handleRoleLoaded(roleDetails);
        }
        mobx.runInAction(() => {
            this.isSaving = false;
            this.isLoading = false;
        });
    };

    @mobx.action
    private handleRoleLoaded = roleDetails => {
        const {name, description} = roleDetails;
        this.roleFormDataVM.setName(name);
        this.roleFormDataVM.setDescription(description);
    };

    @mobx.action
    public handleSubmit = async (intl: IntlShape, roleAction: string) => {
        this.roleFormDataVM.serverErrorMessage = '';
        const isFormInvalid = !this.roleFormDataVM.isFormValid(intl);
        if (isFormInvalid) {
            this.roleFormDataVM.allowToShowValidationErrors();
            return false;
        }
        try {
            this.isSaving = true;
            if (roleAction === RolesAction.Create) {
                const params = {
                    ...this.roleFormDataVM.toApiRoleDTO()
                };
                await this.rolesDAO.createRole(
                    params
                );
                this.trackRoleFormSubmitMixPanel(roleAction);
                showNotification(
                    intl.formatMessage({
                        id: 'oe.role.roleAdded',
                        description: 'Notification about added role',
                        defaultMessage: 'New role added',
                    }));
            } else {
                const params = {
                    linkName: this.linkName,
                    ...this.roleFormDataVM.toApiRoleDTO()
                };
                await this.rolesDAO.updateRole(params);
                this.trackRoleFormSubmitMixPanel(roleAction);
                showNotification(
                    intl.formatMessage({
                        id: 'oe.role.roleUpdated',
                        description: 'Notification about role update',
                        defaultMessage: 'Role updated',
                    }));
            }
            this.fetchRolesList();
            return true;
        } catch (error) {
            this.onCreateRoleError(error);
            return false;
        } finally {
            mobx.runInAction(() => {
                this.isSaving = false;
            });
        }
    };

    @mobx.action
    private onCreateRoleError(error): void {
        const errorMessage = this.errorParsingService.extractMessage(error);
        if (errorMessage) {
            this.roleFormDataVM.serverErrorMessage = errorMessage;
        }
    }

    @mobx.action
    public onDeleteRole = () => {
        this.alertsService.confirm({
            message: (
                <span className='alert-message'>
                    <FormattedMessage
                        id='oe.confirm.areYouSureTo'
                        description='Confirm message'
                        defaultMessage='Are you sure you want to'
                    />
                    <Space />
                    <span className='alert-message--danger'>
                        <FormattedMessage
                            id='oe.role.deleteThisRole'
                            description='Confirm message'
                            defaultMessage='delete this role'
                        />
                    </span>
                    ? <Space/>
                    <br/>
                    <FormattedMessage
                        id='oe.confirm.dangerRoleText'
                        description='Confirm message'
                        defaultMessage='Any users with this Role assigned may lose access permissions.'
                    />
                </span>
            ),
            confirmButtonProps: {
                text: 'Delete Role',
                modifier: 'confirm-warn',
                handler: this.deleteRoleApiCall,
            },
            cancelButtonProps: {
                modifier: 'cancel'
            }
        });
    };

    private completeRoleDeletion = (linkName: string) => {
        this.fetchRolesList();
        this.closeEditRoleForm(linkName);
    };

    private deleteRoleApiCall = async () => {
        try {
            await this.rolesDAO.deleteRole({linkName: this.linkName});
            this.completeRoleDeletion(this.linkName);
        } catch (error) {
            alertServerError('Error', this.errorParsingService.extractMessage(error));
        }
    };

    public trackRoleFormSubmitMixPanel = (roleAction: string) => {
        this.trackingService.get().track(roleAction === RolesAction.Create ? TRACKING_EVENTS.NEW_ROLE : TRACKING_EVENTS.EDIT_ROLE, {});
    };
}

export { RoleFormVM };
